import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, Plus, Trash2, Upload, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import {
  TRANSLATION_SERVICE_GROUPS,
  INTERPRETING_TYPE_KEYS,
  createQuoteRequestId,
  submitQuoteRequest,
  saveQuoteRequestLocal,
} from '../i18n/quoteWizard';
import './QuoteWizardPage.css';

const TOTAL_STEPS = 7;

const emptyForm = () => ({
  civility: '',
  lastName: '',
  firstName: '',
  company: '',
  jobTitle: '',
  email: '',
  phone: '',
  country: '',
  city: '',
  category: 'translation',
  translationService: '',
  interpretingType: '',
  languagePairs: [{ source: 'fr', target: 'ar' }],
  documentType: '',
  domain: '',
  pages: 1,
  words: '',
  fileCount: 1,
  docFormat: 'pdf',
  files: [],
  options: {
    proofreading: false,
    layout: false,
    certified: false,
    sworn: false,
    confidential: false,
  },
  deadline: 'standard',
  preciseDate: '',
  missionType: '',
  missionDate: '',
  startTime: '',
  endTime: '',
  duration: '',
  location: '',
  address: '',
  mode: 'onsite',
  platform: '',
  participants: '',
  equipment: {
    booth: false,
    headsets: false,
    mics: false,
    none: false,
  },
  deliveryMode: 'email',
  instructions: '',
  attestAccurate: false,
  acceptPrivacy: false,
  acceptTerms: false,
});

function FieldError({ message }) {
  if (!message) return null;
  return <span className="qw-error">{message}</span>;
}

export default function QuoteWizardPage() {
  const { t, isRTL } = useLanguage();
  const q = t.quoteWizard;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submittedId, setSubmittedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const groupLabels = useMemo(
    () => ({
      official: q.groupOfficial,
      specialized: q.groupSpecialized,
      digital: q.groupDigital,
    }),
    [q]
  );

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const setOption = (key, value) => {
    setForm((prev) => ({
      ...prev,
      options: { ...prev.options, [key]: value },
    }));
  };

  const setEquipment = (key, value) => {
    setForm((prev) => {
      const equipment = { ...prev.equipment, [key]: value };
      if (key === 'none' && value) {
        equipment.booth = false;
        equipment.headsets = false;
        equipment.mics = false;
      }
      if (key !== 'none' && value) {
        equipment.none = false;
      }
      return { ...prev, equipment };
    });
  };

  const updatePair = (index, key, value) => {
    setForm((prev) => {
      const languagePairs = prev.languagePairs.map((pair, i) =>
        i === index ? { ...pair, [key]: value } : pair
      );
      return { ...prev, languagePairs };
    });
  };

  const addPair = () => {
    setForm((prev) => ({
      ...prev,
      languagePairs: [...prev.languagePairs, { source: 'fr', target: 'en' }],
    }));
  };

  const removePair = (index) => {
    setForm((prev) => ({
      ...prev,
      languagePairs:
        prev.languagePairs.length <= 1
          ? prev.languagePairs
          : prev.languagePairs.filter((_, i) => i !== index),
    }));
  };

  const onFilesChange = (event) => {
    const selected = Array.from(event.target.files || []);
    setForm((prev) => ({
      ...prev,
      files: [...prev.files, ...selected].slice(0, 20),
      fileCount: Math.max(prev.fileCount, selected.length || prev.fileCount),
    }));
    event.target.value = '';
  };

  const removeFile = (index) => {
    setForm((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const validateStep = (current = step) => {
    const nextErrors = {};
    const req = q.required;

    if (current === 0) {
      if (!form.civility.trim()) nextErrors.civility = req;
      if (!form.lastName.trim()) nextErrors.lastName = req;
      if (!form.firstName.trim()) nextErrors.firstName = req;
      if (!form.email.trim()) nextErrors.email = req;
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
        nextErrors.email = req;
      }
      if (!form.phone.trim()) nextErrors.phone = req;
      if (!form.country.trim()) nextErrors.country = req;
    }

    if (current === 1) {
      if (form.category === 'translation' && !form.translationService) {
        nextErrors.translationService = req;
      }
      if (form.category === 'interpreting' && !form.interpretingType) {
        nextErrors.interpretingType = req;
      }
    }

    if (current === 2) {
      form.languagePairs.forEach((pair, index) => {
        if (!pair.source || !pair.target || pair.source === pair.target) {
          nextErrors[`pair_${index}`] = req;
        }
      });
    }

    if (current === 3) {
      if (form.category === 'translation') {
        if (!form.documentType) nextErrors.documentType = req;
        if (!form.domain) nextErrors.domain = req;
        if (!form.docFormat) nextErrors.docFormat = req;
        if (form.deadline === 'date' && !form.preciseDate) {
          nextErrors.preciseDate = req;
        }
      } else {
        if (!form.missionType) nextErrors.missionType = req;
        if (!form.missionDate) nextErrors.missionDate = req;
        if (!form.mode) nextErrors.mode = req;
        if (form.mode === 'remote' && !form.platform) {
          nextErrors.platform = req;
        }
        if (form.mode === 'onsite' && !form.location.trim() && !form.address.trim()) {
          nextErrors.location = req;
        }
      }
    }

    if (current === 4 && form.category === 'translation' && !form.deliveryMode) {
      nextErrors.deliveryMode = req;
    }

    if (current === 6) {
      if (!form.attestAccurate) nextErrors.attestAccurate = req;
      if (!form.acceptPrivacy) nextErrors.acceptPrivacy = req;
      if (!form.acceptTerms) nextErrors.acceptTerms = req;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1));
  };

  const goPrev = () => setStep((s) => Math.max(0, s - 1));

  const handleSubmit = async () => {
    if (!validateStep(6) || submitting) return;
    setSubmitting(true);
    setSubmitError('');

    const id = createQuoteRequestId();
    const { files, ...rest } = form;
    const payload = {
      id,
      ...rest,
      fileNames: files.map((f) => f.name),
      fileMeta: files.map((f) => ({
        name: f.name,
        size: f.size,
        type: f.type,
      })),
    };

    try {
      const request = await submitQuoteRequest(payload, files);
      setSubmittedId(request.id || id);
    } catch (err) {
      // Prefer API; on failure show error (local save keeps a draft copy).
      try {
        saveQuoteRequestLocal(payload);
      } catch (_) {
        /* ignore localStorage failures */
      }
      setSubmitError(q.submitError);
    } finally {
      setSubmitting(false);
    }
  };

  const resetWizard = () => {
    setForm(emptyForm());
    setErrors({});
    setStep(0);
    setSubmittedId(null);
    setSubmitError('');
  };

  if (!q) {
    return (
      <div className="quote-wizard-page">
        <div className="container" style={{ padding: '80px 24px' }}>
          <p>Quote wizard translations missing.</p>
        </div>
      </div>
    );
  }

  if (submittedId) {
    return (
      <div className={`quote-wizard-page ${isRTL ? 'rtl' : ''}`}>
        <header className="qw-hero">
          <div className="container">
            <h1>{q.title}</h1>
          </div>
        </header>
        <section className="section qw-section">
          <div className="container qw-success">
            <CheckCircle size={48} className="qw-success-icon" />
            <h2>{q.successTitle}</h2>
            <p>{q.successText}</p>
            <p className="qw-success-email">{q.successEmail}</p>
            <p className="qw-success-ref">
              {q.successRef}: <strong>{submittedId}</strong>
            </p>
            <div className="qw-success-actions">
              <Link to="/" className="btn btn-secondary">
                {q.backHome}
              </Link>
              <button type="button" className="btn btn-primary" onClick={resetWizard}>
                {q.newRequest}
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const renderStep = () => {
    if (step === 0) {
      return (
        <div className="qw-panel">
          <h2>{q.step1Title}</h2>
          <div className="qw-grid">
            <label className="qw-field">
              <span>
                {q.civility} *
              </span>
              <select
                required
                value={form.civility}
                onChange={(e) => setField('civility', e.target.value)}
              >
                <option value="">—</option>
                {Object.entries(q.civilities || {}).map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
              <FieldError message={errors.civility} />
            </label>
            <label className="qw-field">
              <span>{q.lastName} *</span>
              <input
                value={form.lastName}
                onChange={(e) => setField('lastName', e.target.value)}
                autoComplete="family-name"
              />
              <FieldError message={errors.lastName} />
            </label>
            <label className="qw-field">
              <span>{q.firstName} *</span>
              <input
                value={form.firstName}
                onChange={(e) => setField('firstName', e.target.value)}
                autoComplete="given-name"
              />
              <FieldError message={errors.firstName} />
            </label>
            <label className="qw-field">
              <span>
                {q.company} <em>{q.optional}</em>
              </span>
              <input
                value={form.company}
                onChange={(e) => setField('company', e.target.value)}
                autoComplete="organization"
              />
            </label>
            <label className="qw-field">
              <span>
                {q.jobTitle} <em>{q.optional}</em>
              </span>
              <input
                value={form.jobTitle}
                onChange={(e) => setField('jobTitle', e.target.value)}
                autoComplete="organization-title"
              />
            </label>
            <label className="qw-field">
              <span>{q.email} *</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                autoComplete="email"
              />
              <FieldError message={errors.email} />
            </label>
            <label className="qw-field">
              <span>{q.phone} *</span>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setField('phone', e.target.value)}
                autoComplete="tel"
              />
              <FieldError message={errors.phone} />
            </label>
            <label className="qw-field">
              <span>{q.country} *</span>
              <input
                value={form.country}
                onChange={(e) => setField('country', e.target.value)}
                autoComplete="country-name"
              />
              <FieldError message={errors.country} />
            </label>
            <label className="qw-field">
              <span>
                {q.city} <em>{q.optional}</em>
              </span>
              <input
                value={form.city}
                onChange={(e) => setField('city', e.target.value)}
                autoComplete="address-level2"
              />
            </label>
          </div>
        </div>
      );
    }

    if (step === 1) {
      return (
        <div className="qw-panel">
          <h2>{q.step2Title}</h2>
          <p className="qw-label">{q.category}</p>
          <div className="qw-choice-row">
            <button
              type="button"
              className={`qw-choice ${form.category === 'translation' ? 'active' : ''}`}
              onClick={() => setField('category', 'translation')}
            >
              {q.catTranslation}
            </button>
            <button
              type="button"
              className={`qw-choice ${form.category === 'interpreting' ? 'active' : ''}`}
              onClick={() => setField('category', 'interpreting')}
            >
              {q.catInterpreting}
            </button>
          </div>

          {form.category === 'translation' ? (
            <>
              <p className="qw-label">{q.translationService} *</p>
              {TRANSLATION_SERVICE_GROUPS.map((group) => (
                <div key={group.id} className="qw-service-group">
                  <h3>{groupLabels[group.id]}</h3>
                  <div className="qw-radio-grid">
                    {group.keys.map((key) => (
                      <label key={key} className="qw-radio">
                        <input
                          type="radio"
                          name="translationService"
                          checked={form.translationService === key}
                          onChange={() => setField('translationService', key)}
                        />
                        <span>{q.translationServices[key]}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <FieldError message={errors.translationService} />
            </>
          ) : (
            <>
              <p className="qw-label">{q.interpretingType} *</p>
              <div className="qw-radio-grid">
                {INTERPRETING_TYPE_KEYS.map((key) => (
                  <label key={key} className="qw-radio">
                    <input
                      type="radio"
                      name="interpretingType"
                      checked={form.interpretingType === key}
                      onChange={() => setField('interpretingType', key)}
                    />
                    <span>{q.interpretingTypes[key]}</span>
                  </label>
                ))}
              </div>
              <FieldError message={errors.interpretingType} />
            </>
          )}
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className="qw-panel">
          <h2>{q.step3Title}</h2>
          <div className="qw-pairs">
            {form.languagePairs.map((pair, index) => (
              <div key={index} className="qw-pair">
                <label className="qw-field">
                  <span>{q.sourceLang}</span>
                  <select
                    value={pair.source}
                    onChange={(e) => updatePair(index, 'source', e.target.value)}
                  >
                    {q.languages.map((lang) => (
                      <option key={lang.id} value={lang.id}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="qw-field">
                  <span>{q.targetLang}</span>
                  <select
                    value={pair.target}
                    onChange={(e) => updatePair(index, 'target', e.target.value)}
                  >
                    {q.languages.map((lang) => (
                      <option key={lang.id} value={lang.id}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="qw-icon-btn"
                  onClick={() => removePair(index)}
                  disabled={form.languagePairs.length <= 1}
                  aria-label={q.remove}
                >
                  <Trash2 size={16} />
                </button>
                <FieldError message={errors[`pair_${index}`]} />
              </div>
            ))}
          </div>
          <button type="button" className="btn btn-secondary qw-add-btn" onClick={addPair}>
            <Plus size={16} />
            {q.addLanguage}
          </button>
        </div>
      );
    }

    if (step === 3 && form.category === 'translation') {
      return (
        <div className="qw-panel">
          <h2>{q.step4Title}</h2>
          <div className="qw-grid">
            <label className="qw-field">
              <span>{q.documentType} *</span>
              <select
                value={form.documentType}
                onChange={(e) => setField('documentType', e.target.value)}
              >
                <option value="">—</option>
                {Object.entries(q.documentTypes).map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
              <FieldError message={errors.documentType} />
            </label>
            <label className="qw-field">
              <span>{q.domain} *</span>
              <select value={form.domain} onChange={(e) => setField('domain', e.target.value)}>
                <option value="">—</option>
                {Object.entries(q.domains).map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
              <FieldError message={errors.domain} />
            </label>
            <label className="qw-field">
              <span>{q.pages}</span>
              <input
                type="number"
                min="1"
                value={form.pages}
                onChange={(e) => setField('pages', Number(e.target.value) || '')}
              />
            </label>
            <label className="qw-field">
              <span>
                {q.words} <em>{q.optional}</em>
              </span>
              <input
                type="number"
                min="0"
                value={form.words}
                onChange={(e) => setField('words', e.target.value)}
              />
            </label>
            <label className="qw-field">
              <span>{q.fileCount}</span>
              <input
                type="number"
                min="1"
                value={form.fileCount}
                onChange={(e) => setField('fileCount', Number(e.target.value) || 1)}
              />
            </label>
            <label className="qw-field">
              <span>{q.docFormat} *</span>
              <select
                value={form.docFormat}
                onChange={(e) => setField('docFormat', e.target.value)}
              >
                {Object.entries(q.formats).map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
              <FieldError message={errors.docFormat} />
            </label>
          </div>

          <div className="qw-upload">
            <p className="qw-label">{q.upload}</p>
            <label className="qw-upload-box">
              <Upload size={20} />
              <span>{q.addFiles}</span>
              <small>{q.filesHint}</small>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.zip,.txt,.rtf"
                onChange={onFilesChange}
              />
            </label>
            {form.files.length > 0 && (
              <ul className="qw-file-list">
                {form.files.map((file, index) => (
                  <li key={`${file.name}-${index}`}>
                    <span>{file.name}</span>
                    <button type="button" onClick={() => removeFile(index)} aria-label={q.remove}>
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className="qw-label">{q.options}</p>
          <div className="qw-check-grid">
            {[
              ['proofreading', q.optionProofreading],
              ['layout', q.optionLayout],
              ['certified', q.optionCertified],
              ['sworn', q.optionSworn],
              ['confidential', q.optionConfidential],
            ].map(([key, label]) => (
              <label key={key} className="qw-check">
                <input
                  type="checkbox"
                  checked={form.options[key]}
                  onChange={(e) => setOption(key, e.target.checked)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>

          <p className="qw-label">{q.deadline}</p>
          <div className="qw-choice-row wrap">
            {[
              ['standard', q.deadlineStandard],
              ['priority', q.deadlinePriority],
              ['urgent', q.deadlineUrgent],
              ['date', q.deadlineDate],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`qw-choice ${form.deadline === id ? 'active' : ''}`}
                onClick={() => setField('deadline', id)}
              >
                {label}
              </button>
            ))}
          </div>
          {form.deadline === 'date' && (
            <label className="qw-field qw-field-narrow">
              <span>{q.preciseDate} *</span>
              <input
                type="date"
                value={form.preciseDate}
                onChange={(e) => setField('preciseDate', e.target.value)}
              />
              <FieldError message={errors.preciseDate} />
            </label>
          )}
        </div>
      );
    }

    if (step === 3 && form.category === 'interpreting') {
      return (
        <div className="qw-panel">
          <h2>{q.step4Title}</h2>
          <div className="qw-grid">
            <label className="qw-field">
              <span>{q.missionType} *</span>
              <select
                value={form.missionType}
                onChange={(e) => setField('missionType', e.target.value)}
              >
                <option value="">—</option>
                {Object.entries(q.missionTypes).map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
              <FieldError message={errors.missionType} />
            </label>
            <label className="qw-field">
              <span>{q.missionDate} *</span>
              <input
                type="date"
                value={form.missionDate}
                onChange={(e) => setField('missionDate', e.target.value)}
              />
              <FieldError message={errors.missionDate} />
            </label>
            <label className="qw-field">
              <span>{q.startTime}</span>
              <input
                type="time"
                value={form.startTime}
                onChange={(e) => setField('startTime', e.target.value)}
              />
            </label>
            <label className="qw-field">
              <span>{q.endTime}</span>
              <input
                type="time"
                value={form.endTime}
                onChange={(e) => setField('endTime', e.target.value)}
              />
            </label>
            <label className="qw-field">
              <span>{q.duration}</span>
              <input
                value={form.duration}
                onChange={(e) => setField('duration', e.target.value)}
                placeholder="2h"
              />
            </label>
            <label className="qw-field">
              <span>{q.participants}</span>
              <input
                type="number"
                min="1"
                value={form.participants}
                onChange={(e) => setField('participants', e.target.value)}
              />
            </label>
          </div>

          <p className="qw-label">{q.mode} *</p>
          <div className="qw-choice-row">
            <button
              type="button"
              className={`qw-choice ${form.mode === 'onsite' ? 'active' : ''}`}
              onClick={() => setField('mode', 'onsite')}
            >
              {q.modeOnsite}
            </button>
            <button
              type="button"
              className={`qw-choice ${form.mode === 'remote' ? 'active' : ''}`}
              onClick={() => setField('mode', 'remote')}
            >
              {q.modeRemote}
            </button>
          </div>

          {form.mode === 'onsite' ? (
            <div className="qw-grid">
              <label className="qw-field">
                <span>{q.location} *</span>
                <input
                  value={form.location}
                  onChange={(e) => setField('location', e.target.value)}
                />
                <FieldError message={errors.location} />
              </label>
              <label className="qw-field">
                <span>{q.address}</span>
                <input
                  value={form.address}
                  onChange={(e) => setField('address', e.target.value)}
                />
              </label>
            </div>
          ) : (
            <label className="qw-field qw-field-narrow">
              <span>{q.platform} *</span>
              <select
                value={form.platform}
                onChange={(e) => setField('platform', e.target.value)}
              >
                <option value="">—</option>
                {Object.entries(q.platforms).map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
              <FieldError message={errors.platform} />
            </label>
          )}

          <p className="qw-label">{q.equipment}</p>
          <div className="qw-check-grid">
            {[
              ['booth', q.equipBooth],
              ['headsets', q.equipHeadsets],
              ['mics', q.equipMics],
              ['none', q.equipNone],
            ].map(([key, label]) => (
              <label key={key} className="qw-check">
                <input
                  type="checkbox"
                  checked={form.equipment[key]}
                  onChange={(e) => setEquipment(key, e.target.checked)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (step === 4) {
      if (form.category === 'interpreting') {
        return (
          <div className="qw-panel">
            <h2>{q.step5Title}</h2>
            <p className="qw-muted">
              {form.mode === 'remote' ? q.modeRemote : q.modeOnsite}
              {form.platform ? ` · ${q.platforms[form.platform] || form.platform}` : ''}
              {form.location ? ` · ${form.location}` : ''}
            </p>
            <p className="qw-hint">
              {q.deliveryMode}: {q.deliveryModes.email}
            </p>
          </div>
        );
      }
      return (
        <div className="qw-panel">
          <h2>{q.step5Title}</h2>
          <p className="qw-label">{q.deliveryMode} *</p>
          <div className="qw-radio-grid">
            {Object.entries(q.deliveryModes).map(([id, label]) => (
              <label key={id} className="qw-radio">
                <input
                  type="radio"
                  name="deliveryMode"
                  checked={form.deliveryMode === id}
                  onChange={() => setField('deliveryMode', id)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          <FieldError message={errors.deliveryMode} />
        </div>
      );
    }

    if (step === 5) {
      return (
        <div className="qw-panel">
          <h2>{q.step6Title}</h2>
          <label className="qw-field">
            <span>
              {q.instructions} <em>{q.optional}</em>
            </span>
            <textarea
              rows={6}
              value={form.instructions}
              onChange={(e) => setField('instructions', e.target.value)}
              placeholder={q.instructionsPlaceholder}
            />
          </label>
        </div>
      );
    }

    const serviceLabel =
      form.category === 'translation'
        ? q.translationServices[form.translationService]
        : q.interpretingTypes[form.interpretingType];

    return (
      <div className="qw-panel">
        <h2>{q.step7Title}</h2>
        <div className="qw-summary">
          <h3>{q.summary}</h3>
          <ul>
            <li>
              <strong>
                {form.civility ? `${q.civilities?.[form.civility] || form.civility} ` : ''}
                {form.firstName} {form.lastName}
              </strong>{' '}
              — {form.email} — {form.phone}
            </li>
            <li>
              {form.category === 'translation' ? q.catTranslation : q.catInterpreting}
              {serviceLabel ? ` · ${serviceLabel}` : ''}
            </li>
            <li>
              {form.languagePairs
                .map((pair) => {
                  const src = q.languages.find((l) => l.id === pair.source)?.label || pair.source;
                  const tgt = q.languages.find((l) => l.id === pair.target)?.label || pair.target;
                  return `${src} → ${tgt}`;
                })
                .join(' · ')}
            </li>
            {form.category === 'translation' && (
              <li>
                {q.documentTypes[form.documentType] || form.documentType}
                {form.domain ? ` · ${q.domains[form.domain]}` : ''}
                {form.deliveryMode ? ` · ${q.deliveryModes[form.deliveryMode]}` : ''}
              </li>
            )}
            {form.category === 'interpreting' && (
              <li>
                {q.missionTypes[form.missionType] || form.missionType}
                {form.missionDate ? ` · ${form.missionDate}` : ''}
              </li>
            )}
          </ul>
        </div>

        <div className="qw-check-stack">
          <label className="qw-check">
            <input
              type="checkbox"
              checked={form.attestAccurate}
              onChange={(e) => setField('attestAccurate', e.target.checked)}
            />
            <span>{q.attestAccurate}</span>
          </label>
          <FieldError message={errors.attestAccurate} />
          <label className="qw-check">
            <input
              type="checkbox"
              checked={form.acceptPrivacy}
              onChange={(e) => setField('acceptPrivacy', e.target.checked)}
            />
            <span>
              <Link to="/privacy">{q.acceptPrivacy}</Link>
            </span>
          </label>
          <FieldError message={errors.acceptPrivacy} />
          <label className="qw-check">
            <input
              type="checkbox"
              checked={form.acceptTerms}
              onChange={(e) => setField('acceptTerms', e.target.checked)}
            />
            <span>
              <Link to="/legal">{q.acceptTerms}</Link>
            </span>
          </label>
          <FieldError message={errors.acceptTerms} />
        </div>
      </div>
    );
  };

  return (
    <div className={`quote-wizard-page ${isRTL ? 'rtl' : ''}`}>
      <header className="qw-hero">
        <div className="container">
          <h1>{q.title}</h1>
          <p>{q.subtitle}</p>
        </div>
      </header>

      <section className="section qw-section">
        <div className="container qw-layout">
          <nav className="qw-steps" aria-label={q.title}>
            {q.steps.map((label, index) => (
              <button
                key={label}
                type="button"
                className={`qw-step ${index === step ? 'active' : ''} ${
                  index < step ? 'done' : ''
                }`}
                onClick={() => {
                  if (index <= step) setStep(index);
                }}
              >
                <span className="qw-step-num">{index + 1}</span>
                <span className="qw-step-label">{label}</span>
              </button>
            ))}
          </nav>

          <div className="qw-main">
            {renderStep()}

            {submitError && <p className="qw-submit-error">{submitError}</p>}

            <div className="qw-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={goPrev}
                disabled={step === 0 || submitting}
              >
                {isRTL ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
                {q.prev}
              </button>

              {step < TOTAL_STEPS - 1 ? (
                <button type="button" className="btn btn-primary" onClick={goNext}>
                  {q.next}
                  {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? q.submitting : q.submit}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
